import { useState } from "react";
import { Input, Tag, message, Modal as AntModal } from "antd";
import { Button } from "@/components/ui";

interface SendTestEmailModalProps {
  open: boolean;
  onClose: () => void;
  onSend: (emails: string[]) => void;
  loading?: boolean;
}

const SendTestEmailModal = ({
  open,
  onClose,
  onSend,
  loading = false,
}: SendTestEmailModalProps) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Check if user typed a semicolon or pressed enter
    if (value.endsWith(";") || value.endsWith(",")) {
      const email = value.slice(0, -1).trim();
      if (email) {
        addEmail(email);
      }
    } else {
      setInputValue(value);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      addEmail(inputValue.trim());
    }
  };

  const addEmail = (email: string) => {
    if (!validateEmail(email)) {
      message.error("Please enter a valid email address");
      return;
    }

    if (emails.length >= 5) {
      message.warning("Maximum 5 recipients allowed");
      return;
    }

    if (emails.includes(email)) {
      message.warning("Email already added");
      return;
    }

    setEmails([...emails, email]);
    setInputValue("");
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails(emails.filter((email) => email !== emailToRemove));
  };

  const handleSend = () => {
    if (emails.length === 0) {
      message.error("Please add at least one email address");
      return;
    }
    onSend(emails);
  };

  const handleClose = () => {
    setEmails([]);
    setInputValue("");
    onClose();
  };

  return (
    <AntModal
      title="Send Test Email"
      open={open}
      onCancel={handleClose}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            loading={loading}
            disabled={emails.length === 0}
          >
            Send Test Email
          </Button>
        </div>
      }
      width={500}
      centered
    >
      <div className="py-2">
        <p className="text-sm text-black/65 mb-4">
          Send email to specific recipients to test the campaign.
        </p>

        <div className="mb-2">
          <label className="text-sm font-medium text-black/85 mb-2 block">
            Emails
          </label>
          <div className="border rounded-lg p-2 min-h-[80px] bg-white focus-within:border-blue-500 transition-colors">
            <div className="flex flex-wrap gap-2 mb-2">
              {emails.map((email) => (
                <Tag
                  key={email}
                  closable
                  onClose={() => handleRemoveEmail(email)}
                  className="flex items-center"
                >
                  {email}
                </Tag>
              ))}
            </div>
            <Input
              placeholder="Enter emails"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              bordered={false}
              className="p-0"
              disabled={emails.length >= 5}
            />
          </div>
        </div>

        <p className="text-xs text-black/45">
          Max. 5 recipients, separate by ;
        </p>
      </div>
    </AntModal>
  );
};

export default SendTestEmailModal;
